/**
 * Remembers the email used for the latest successful web signup so the auth
 * callback can prefill the "send a fresh link" form on this device. The value
 * is tied to an opaque ref that is also put on that signup's confirmation
 * link, so a later or failed signup cannot prefill the wrong address. Stored
 * only in this browser and cleared once a session starts from the callback.
 */
export const PENDING_SIGNUP_EMAIL_KEY = 'jp_pending_signup_email';

type PendingSignup = { email: string; ref: string };

function readPendingSignup(): PendingSignup | null {
  try {
    const raw = window.localStorage.getItem(PENDING_SIGNUP_EMAIL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingSignup>;
    if (typeof parsed.email !== 'string' || typeof parsed.ref !== 'string') return null;
    return { email: parsed.email, ref: parsed.ref };
  } catch {
    return null;
  }
}

export function createSignupRef(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
  }
}

export function savePendingSignupEmail(email: string, ref: string): void {
  try {
    window.localStorage.setItem(PENDING_SIGNUP_EMAIL_KEY, JSON.stringify({ email, ref }));
  } catch {
    // Ignore storage failures.
  }
}

/** Ref saved for this email, so a resend links back to the same entry. */
export function getPendingSignupRef(email: string): string | null {
  const pending = readPendingSignup();
  return pending && pending.email === email ? pending.ref : null;
}

/** Email saved for exactly this signup link ref, or an empty string. */
export function readPendingSignupEmail(ref: string | null): string {
  if (!ref) return '';
  const pending = readPendingSignup();
  return pending && pending.ref === ref ? pending.email : '';
}

export function clearPendingSignupEmail(): void {
  try {
    window.localStorage.removeItem(PENDING_SIGNUP_EMAIL_KEY);
  } catch {
    // Ignore storage failures.
  }
}
